/** Minutes without a successful poll before an active, mapped connection counts as stale. */
export const STALE_AFTER_MINUTES = 30

export type ConnectionHealthState = "ok" | "inactive" | "unmapped" | "backing-off" | "stale"

export type ConnectionHealth = { state: ConnectionHealthState; reason: string }

type Backoff = { backoffUntil?: string | null } | null | undefined

/** The fields of a Data Connection that its health depends on. */
export type ConnectionHealthInput = {
  status: string
  lastQueried?: string | null
  activeMappingCount: number
  dataConnector?: { runState?: Backoff; pollSchedule?: Backoff } | null
}

/** Database timestamps such as monitoring_instance.last_queried carry no offset; they are UTC. */
export function parseUtc(value: string): Date {
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(value)
  return new Date(hasOffset ? value : `${value.replace(" ", "T")}Z`)
}

function isInFuture(value: string | null | undefined, now: Date): boolean {
  return Boolean(value) && parseUtc(value as string).getTime() > now.getTime()
}

export function connectionHealth(connection: ConnectionHealthInput, now: Date = new Date()): ConnectionHealth {
  if (connection.status !== "active") {
    return { state: "inactive", reason: "This connection is inactive, so it isn't polled." }
  }

  if (connection.activeMappingCount === 0) {
    return { state: "unmapped", reason: "Active but has no mapped assets, so the worker skips it." }
  }

  const { runState, pollSchedule } = connection.dataConnector ?? {}
  if (isInFuture(runState?.backoffUntil, now) || isInFuture(pollSchedule?.backoffUntil, now)) {
    return {
      state: "backing-off",
      reason: "Its Data Connector is backing off after errors. Every connection on that Data Connector is paused.",
    }
  }

  if (!connection.lastQueried) {
    return { state: "stale", reason: "Active and mapped, but has never been queried." }
  }

  const minutes = (now.getTime() - parseUtc(connection.lastQueried).getTime()) / 60_000
  if (minutes > STALE_AFTER_MINUTES) {
    return { state: "stale", reason: `Not queried for ${Math.floor(minutes)} minutes.` }
  }

  return { state: "ok", reason: "Active, mapped and queried recently." }
}
